import React, { useState } from 'react';
import { X, AlertTriangle, Code, FileText, Clock } from 'lucide-react';

interface ErrorInfo {
  message: string;
  stack?: string;
  name?: string;
  fileName?: string;
  lineNumber?: number;
  columnNumber?: number;
  timestamp: Date;
  componentStack?: string;
}

interface DebugErrorDisplayProps {
  errors: ErrorInfo[];
  onClear: () => void;
  onToggleDebug: () => void;
  debugMode: boolean;
}

const DebugErrorDisplay: React.FC<DebugErrorDisplayProps> = ({
  errors,
  onClear,
  onToggleDebug,
  debugMode
}) => {
  const [selectedError, setSelectedError] = useState<ErrorInfo | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);

  if (!debugMode || errors.length === 0) return null;

  return (
    <>
      {/* Error Notification Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white p-3 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-semibold">
              {errors.length} Error{errors.length > 1 ? 's' : ''} Detected
            </span>
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="ml-2 px-2 py-1 bg-red-700 hover:bg-red-800 rounded text-sm"
            >
              {isMinimized ? 'Show' : 'Hide'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onToggleDebug}
              className="px-3 py-1 bg-red-700 hover:bg-red-800 rounded text-sm"
            >
              Disable Debug
            </button>
            <button
              onClick={onClear}
              className="px-3 py-1 bg-red-700 hover:bg-red-800 rounded text-sm"
            >
              Clear All
            </button>
          </div>
        </div>
      </div>

      {/* Error Details Panel */}
      {!isMinimized && (
        <div className="fixed top-16 left-4 right-4 z-40 bg-white border border-red-300 rounded-lg shadow-xl max-h-96 overflow-y-auto">
          <div className="p-4">
            <div className="space-y-3">
              {errors.map((error, index) => (
                <div
                  key={index}
                  className="border border-red-200 rounded p-3 bg-red-50 cursor-pointer hover:bg-red-100"
                  onClick={() => setSelectedError(error)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Code className="w-4 h-4 text-red-600" />
                        <span className="font-mono text-sm text-red-800">
                          {error.name || 'Error'}
                        </span>
                        <Clock className="w-3 h-3 text-gray-500" />
                        <span className="text-xs text-gray-500">
                          {error.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-800 font-medium">
                        {error.message}
                      </p>
                      {error.fileName && (
                        <div className="flex items-center gap-1 mt-1">
                          <FileText className="w-3 h-3 text-gray-500" />
                          <span className="text-xs text-gray-600">
                            {error.fileName}
                            {error.lineNumber && `:${error.lineNumber}`}
                            {error.columnNumber && `:${error.columnNumber}`}
                          </span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Remove this specific error - functionality can be added later
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Detailed Error Modal */}
      {selectedError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto ml-64">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  Error Details
                </h2>
                <button
                  onClick={() => setSelectedError(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Error Type</h3>
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                    {selectedError.name || 'Unknown Error'}
                  </code>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Message</h3>
                  <p className="bg-red-50 border border-red-200 rounded p-3 text-red-800">
                    {selectedError.message}
                  </p>
                </div>

                {selectedError.fileName && (
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Location</h3>
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm block">
                      {selectedError.fileName}
                      {selectedError.lineNumber && `:${selectedError.lineNumber}`}
                      {selectedError.columnNumber && `:${selectedError.columnNumber}`}
                    </code>
                  </div>
                )}

                {selectedError.stack && (
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Stack Trace</h3>
                    <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto whitespace-pre-wrap">
                      {selectedError.stack}
                    </pre>
                  </div>
                )}

                {selectedError.componentStack && (
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Component Stack</h3>
                    <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto whitespace-pre-wrap">
                      {selectedError.componentStack}
                    </pre>
                  </div>
                )}

                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Timestamp</h3>
                  <p className="text-sm text-gray-600">
                    {selectedError.timestamp.toLocaleString()}
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Suggestions</h3>
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
                      <li>Check the stack trace for the source of the error</li>
                      <li>Verify that all required dependencies are installed</li>
                      <li>Ensure proper error handling is in place</li>
                      <li>Check browser console for additional details</li>
                      <li>Review recent code changes that might have introduced this error</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DebugErrorDisplay;